import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  getRoutesSummary,
  getVehicleTrail,
  RouteFleetSummary,
  RouteSummaryVehicle,
  RouteTrail,
} from "../../api";
import { Icon } from "../../icons";

function formatDuration(seconds: number, t: TFunction): string {
  if (!seconds) return t("routes.durationZero");
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h > 0) return t("routes.durationHourMin", { h, m });
  return t("routes.durationMin", { m });
}

const HOURS_OPTIONS = [
  { value: 6, labelKey: "routes.hours6" },
  { value: 24, labelKey: "routes.hours24" },
  { value: 72, labelKey: "routes.hours72" },
  { value: 168, labelKey: "routes.hours168" },
];

export function RoutesPage() {
  const { t, i18n } = useTranslation(["dashboard", "common"]);
  const [hours, setHours] = useState(24);
  const [summary, setSummary] = useState<RouteFleetSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [trail, setTrail] = useState<RouteTrail | null>(null);
  const [trailLoading, setTrailLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getRoutesSummary(undefined, hours)
      .then((s) => {
        setSummary(s);
        if (s.vehicles.length > 0 && !selectedVehicle) {
          setSelectedVehicle(s.vehicles[0].vehicle_id);
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours]);

  useEffect(() => {
    if (!selectedVehicle) return;
    setTrailLoading(true);
    getVehicleTrail(selectedVehicle, hours)
      .then(setTrail)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setTrailLoading(false));
  }, [selectedVehicle, hours]);

  return (
    <div className="page">
      <div className="page-head-row">
        <div>
          <h2 style={{ marginTop: 0 }}>
            <Icon name="map" /> {t("routes.title")}
          </h2>
          <p className="muted">{t("routes.intro")}</p>
        </div>
        <div>
          <label className="muted small" style={{ marginRight: "0.5rem" }}>
            {t("routes.timeRange")}
          </label>
          <select
            value={hours}
            onChange={(e) => setHours(parseInt(e.target.value, 10))}
          >
            {HOURS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.labelKey)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <p className="muted">{t("common:actions.loading")}</p>
      ) : !summary || summary.vehicles.length === 0 ? (
        <div className="empty-card">
          <Icon name="map" /> {t("routes.emptyGps")}
        </div>
      ) : (
        <>
          <h3>{t("routes.fleetSummary")}</h3>
          <table>
            <thead>
              <tr>
                <th>{t("routes.th.vehicle")}</th>
                <th>{t("routes.th.gpsFix")}</th>
                <th>{t("routes.th.distance")}</th>
                <th>{t("routes.th.moving")}</th>
                <th>{t("routes.th.stopped")}</th>
                <th>{t("routes.th.avgSpeed")}</th>
                <th>{t("routes.th.maxSpeed")}</th>
                <th>{t("routes.th.lastSeen")}</th>
              </tr>
            </thead>
            <tbody>
              {summary.vehicles.map((v) => (
                <VehicleRow
                  key={v.vehicle_id}
                  vehicle={v}
                  selected={v.vehicle_id === selectedVehicle}
                  onSelect={() => setSelectedVehicle(v.vehicle_id)}
                  t={t}
                  locale={i18n.resolvedLanguage}
                />
              ))}
            </tbody>
          </table>

          {selectedVehicle && (
            <>
              <h3 style={{ marginTop: "1.5rem" }}>
                {t("routes.trailTitle")}: <code>{selectedVehicle}</code>
              </h3>
              {trailLoading ? (
                <p className="muted">{t("routes.trailLoading")}</p>
              ) : trail && trail.points.length > 0 ? (
                <TrailMap trail={trail} startLabel={t("routes.start")} endLabel={t("routes.lastLocation")} />
              ) : (
                <div className="empty-card">
                  <Icon name="map" /> {t("routes.noTrailPoints")}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function VehicleRow({
  vehicle: v,
  selected,
  onSelect,
  t,
  locale,
}: {
  vehicle: RouteSummaryVehicle;
  selected: boolean;
  onSelect: () => void;
  t: TFunction;
  locale?: string;
}) {
  return (
    <tr
      onClick={onSelect}
      style={{
        cursor: "pointer",
        background: selected ? "rgba(42,95,255,0.06)" : undefined,
      }}
    >
      <td>
        <strong>{v.vehicle_id}</strong>
      </td>
      <td>{v.point_count}</td>
      <td>{v.distance_km.toFixed(1)} km</td>
      <td>{formatDuration(v.moving_seconds, t)}</td>
      <td>{formatDuration(v.stopped_seconds, t)}</td>
      <td>{v.avg_speed_kph.toFixed(1)} km/h</td>
      <td>{v.max_speed_kph.toFixed(1)} km/h</td>
      <td className="muted small">
        {v.last_seen ? new Date(v.last_seen).toLocaleString(locale) : "—"}
      </td>
    </tr>
  );
}

function TrailMap({ trail, startLabel, endLabel }: { trail: RouteTrail; startLabel: string; endLabel: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  const latlngs = useMemo<[number, number][]>(
    () => trail.points.map((p) => [p.lat, p.lon]),
    [trail],
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (latlngs.length === 0) return;
    const polyline = L.polyline(latlngs, {
      color: "#2a5fff",
      weight: 4,
      opacity: 0.85,
    }).addTo(layer);
    L.circleMarker(latlngs[0], {
      radius: 7,
      color: "#10b981",
      fillColor: "#10b981",
      fillOpacity: 0.9,
    })
      .bindTooltip(startLabel, { permanent: false })
      .addTo(layer);
    L.circleMarker(latlngs[latlngs.length - 1], {
      radius: 7,
      color: "#f43f5e",
      fillColor: "#f43f5e",
      fillOpacity: 0.9,
    })
      .bindTooltip(endLabel, { permanent: false })
      .addTo(layer);
    map.fitBounds(polyline.getBounds(), { padding: [24, 24] });
  }, [latlngs, startLabel, endLabel]);

  return <div ref={containerRef} className="trail-map" />;
}
